import BaseEntity from "./BaseEntity";
import GameState, { getXYindex } from "../state/gameState";
import Phaser from "phaser";
import { BOARD_SIZE, GameEvents, layerDepthDiff } from "../constants";
import { Door, EntityKind, Entities } from "../state/entityKinds";
import data from "../artConfig.json";
import { RulesPush } from "../state/rules/canBePushedOnto";
import { SoundPlayer } from "../sounds/SoundPlayer";

export default class DoorEntity extends BaseEntity {
  private sprite: Phaser.GameObjects.Sprite;
  private isDoorOpen: boolean;
  private isOpposite: boolean;
  private isDirty: boolean;
  constructor(
    gameState: GameState,
    scene: Phaser.Scene,
    x: number,
    y: number,
    triggerIdListener: number,
    isOpposite: boolean
  ) {
    super(gameState, scene);
    this.isOpposite = isOpposite;
    this.sprite = this.scene.add.sprite(
      x * BOARD_SIZE,
      y * BOARD_SIZE,
      "atlas",
      "DoorBlock/DoorBlock1"
    );

    if (isOpposite) {
      this.sprite.setTint(0xe34262);
    }
    this.isDirty = true;

    const entity: Door = {
      kind: EntityKind.Door,
      x,
      y,
      id: this.id,
      isOpen: isOpposite,
      triggerIdListener,
      isOpposite: isOpposite,
      entity: () => this,
    };

    this.undo(entity);

    this.scene.events.on(GameEvents.PressurePlateState, this.toggleDoor, this);

    this.isDoorOpen = false;

    this.scene.events.on("update", this.update, this);
    this.scene.events.on(GameEvents.TurnFinished, this.endOfTurnCheck, this);

    this.scene.events.once("destroy", () => {
      this.scene.events.removeListener(
        GameEvents.PressurePlateState,
        this.toggleDoor,
        this
      );
      this.scene.events.removeListener(
        GameEvents.TurnFinished,
        this.endOfTurnCheck,
        this
      );
      this.scene.events.removeListener("update", this.update, this);
    });

    gameState.silentMutateGameState((state) => {
      this.idx = getXYindex(x, y);
      state.board[this.idx].push(entity);
    });
  }

  toggleDoor(
    isOpen: boolean,
    {
      triggerId,
      fromX,
      fromY,
      force,
    }: { triggerId: number; fromX: number; fromY: number; force: boolean }
  ) {
    const id = triggerId;
    const entity = this.entityImmutable as Door;
    if (entity.triggerIdListener !== id) {
      return;
    }
    if (
      entity.isOpen &&
      this.state.turn_gameState.board[this.idx as number].some(
        (e: Entities) =>
          (e.id !== this.id &&
            e.kind !== EntityKind.PlayerBullet &&
            [
              RulesPush.OverlapResolution.Stop,
              RulesPush.OverlapResolution.Push,
            ].includes(RulesPush.canBePushedOnto(e))) ||
          e.kind === EntityKind.PlayerBody
      )
    ) {
      this.isDoorOpen = !this.isOpposite;
      if (!force) {
        return;
      }
    } else {
      this.isDoorOpen = this.isDoorOpen || isOpen;
    }
    const isDoorReallyOpen = this.isOpposite
      ? !this.isDoorOpen
      : this.isDoorOpen;
    if ((isDoorReallyOpen && this.isDirty) || force) {
      this.isDirty = false;
      this.scene.events.emit(GameEvents.FX_DoorToggled, {
        isOpenSignal: isOpen,
        fromX,
        fromY,
        toX: this.entityImmutable.x,
        toY: this.entityImmutable.y,
      });
    }
  }

  endOfTurnCheck() {
    let door_changed = false;
    if (this.isOpposite) {
      this.isDoorOpen = !this.isDoorOpen;
    }
    if (!this.isDoorOpen) {
      this.isDirty = true;
    }

    this.state.mutateGameState((state) => {
      let door = state.board[this.idx as number].find(
        ({ id }) => id === this.id
      ) as Door;
      if (!door) {
        throw new Error("lost ref to door");
      }
      if (
        door.isOpen &&
        state.board[this.idx as number].some(
          (e: Entities) =>
            e.id !== this.id &&
            [
              RulesPush.OverlapResolution.Stop,
              RulesPush.OverlapResolution.Push,
            ].includes(RulesPush.canBePushedOnto(e))
        )
      ) {
        door.isOpen = true;
        door_changed = door.isOpen;
      } else {
        door.isOpen = this.isDoorOpen;
        door_changed = door.isOpen;
      }
    });
    if (door_changed) {
      this.sprite.setDepth(this.entityImmutable.y + layerDepthDiff.BUTTON);
    } else {
      this.sprite.setDepth(this.entityImmutable.y + layerDepthDiff.WALLS);
    }

    this.isDoorOpen = false;
  }

  update() {
    const { isOpen, y } = this.entityImmutable as Door;
    if (isOpen) {
      this.sprite.setDepth(y + layerDepthDiff.BUTTON);
    } else {
      this.sprite.setDepth(y + layerDepthDiff.WALLS);
    }
    if (
      !this.sprite.anims.isPlaying &&
      isOpen &&
      this.sprite.frame.name === "DoorBlock/DoorBlock1"
    ) {
      SoundPlayer.doorChangeUpSFX(this.scene);
      this.sprite.anims.play(data.animations.doorBlock.key);
    } else if (
      !this.sprite.anims.isPlaying &&
      !isOpen &&
      this.sprite.frame.name === "DoorBlock/DoorBlock5"
    ) {
      this.sprite.anims.playReverse(data.animations.doorBlock.key);
      SoundPlayer.doorChangeDownSFX(this.scene);
    }
  }

  undo(e: Door) {
    this.sprite.anims.stop();
    if (!e.isOpen) {
      this.isDirty = true;
    } else {
      this.isDirty = false;
    }
    if (e.isOpen) {
      this.sprite.setFrame("DoorBlock/DoorBlock5");
      this.sprite.setDepth(e.y + layerDepthDiff.BUTTON);
    } else {
      this.sprite.setFrame("DoorBlock/DoorBlock1");
      this.sprite.setDepth(e.y + layerDepthDiff.WALLS);
    }
  }
}
